import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./settings-drawer.css";
import {
  ChevronLeftIconV1,
  ChevronRightIconV1,
  ExternalLinkIconV1,
} from "images/image-component";

export interface DrawerItem {
  label: string;
  icon?: React.ReactNode;
  /** Fires for action rows. Ignored when `submenu` is set. */
  onClick?: () => void;
  /** Turns the row into a chevron that pushes a sub-view instead of acting. */
  submenu?: DrawerView;
  /** Trailing value, e.g. the active token symbol. */
  value?: string;
  /** Trailing pill, e.g. a count or a warning. */
  badge?: string;
  /** Second line under the label. */
  hint?: string;
  variant?: "default" | "danger";
  disabled?: boolean;
  /**
   * Marks the active choice when the section is a picker, not a list of actions. A section where
   * any item sets this is exposed as a menu of single-choice items: arrow keys move focus and
   * Enter/Space chooses. Deliberately NOT an ARIA radiogroup — that pattern selects as the arrows
   * move, and since choosing dismisses the drawer the first arrow press would close it.
   */
  selected?: boolean;
  /** Trailing glyph becomes an external-link arrow. */
  external?: boolean;
}

export interface DrawerSection {
  label?: string;
  items: DrawerItem[];
}

export interface DrawerView {
  title: string;
  sections: DrawerSection[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  sections: DrawerSection[];
}

const FOCUSABLE =
  'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal overlays sit above the drawer at z-index 1300. Every live modal in all three apps portals
 * to document.body with this class (usersApp/merchantsApp CustomModalV1, issuerApp CustomModal),
 * so its presence in the DOM is a reliable "something owns the screen above us" test.
 */
const MODAL_ABOVE = ".v1-modal-overlay";

function isPicker(section: DrawerSection): boolean {
  return section.items.some((it) => it.selected !== undefined);
}

function SettingsDrawer({
  isOpen,
  onClose,
  title = "Settings",
  sections,
}: Props) {
  // A PATH of row labels, not a snapshot of the sub-view. Storing the view object froze it: a
  // picker inside it kept rendering the selection that was live when it was opened. Re-resolving
  // against the current `sections` every render keeps a sub-view as fresh as the root.
  const [path, setPath] = useState<string[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  // Resolves as deep as the path is still valid. A row that stopped existing (the token picker
  // disappears when a network offers one token) collapses to the deepest view that does exist
  // instead of stranding the drawer on a dead level — self-correcting, so no effect is needed.
  const { view, depth } = useMemo(() => {
    let current: DrawerView = { title, sections };
    let resolved = 0;
    for (const label of path) {
      const next = current.sections
        .reduce<DrawerItem[]>((acc, s) => acc.concat(s.items), [])
        .find((it) => it.label === label && it.submenu)?.submenu;
      if (!next) break;
      current = next;
      resolved += 1;
    }
    return {
      view: {
        ...current,
        // A section whose rows are all conditional can arrive empty — rendering it would leave a
        // stray heading and separator behind.
        sections: current.sections.filter((s) => s.items.length > 0),
      },
      depth: resolved,
    };
  }, [title, sections, path]);

  const atRoot = depth === 0;
  const pop = useCallback(
    () => setPath((p) => p.slice(0, Math.max(0, depth - 1))),
    [depth],
  );

  // Reopening always starts at the root view; a stale sub-view would otherwise be the first thing
  // the next visit shows.
  useEffect(() => {
    if (!isOpen) setPath([]);
  }, [isOpen]);

  // A CLASS, not body.style.overflow, on purpose. The modals (CustomModalV1) lock scroll by saving
  // and restoring the inline style, and a modal can be on screen at the same time as the drawer —
  // a session-timeout warning, or one this drawer opened. Two naive save/restore locks are
  // order-dependent: close the drawer while a modal is still up and the modal's later restore puts
  // `overflow: hidden` back with nothing on screen, wedging the page. Writing a class the modals
  // never read keeps the two locks independent, so any interleaving unwinds correctly.
  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add("gp-drawer-locked");
    return () => document.body.classList.remove("gp-drawer-locked");
  }, [isOpen]);

  // Escape unwinds one level at a time so it mirrors the Back button rather than discarding the
  // whole drawer from a sub-view. Ignored while a modal is above us — that dialog owns Escape.
  useEffect(() => {
    if (!isOpen) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (document.querySelector(MODAL_ABOVE)) return;
      if (depth > 0) pop();
      else onClose();
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, depth, pop, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    return () => {
      // The settings button is the sensible landing spot, and it is what had focus on open. Skip
      // it when something above has taken over — a modal a row just opened owns focus now.
      if (document.querySelector(MODAL_ABOVE)) return;
      restoreRef.current?.focus?.();
    };
  }, [isOpen]);

  // Re-focus on every level change, not just on open. Pushing a sub-view unmounts the row that was
  // focused, which drops activeElement to <body> — and a trap that only wraps at the first and
  // last element does nothing from there, so Tab would walk out of the drawer into the page behind.
  useEffect(() => {
    if (!isOpen) return;
    if (document.querySelector(MODAL_ABOVE)) return;
    panelRef.current?.focus();
  }, [isOpen, depth]);

  const trapTab = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    // Never seal focus in while a modal is above: the session-timeout warning renders over the
    // drawer, and a keyboard-only user has to be able to reach its buttons to extend the session.
    if (document.querySelector(MODAL_ABOVE)) return;
    const nodes = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement;
    const inside = active instanceof Node && panel.contains(active);
    if (!inside) {
      // Focus fell outside (a just-unmounted row leaves it on <body>): pull it back in.
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && (active === first || active === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  const handleItem = useCallback(
    (item: DrawerItem, e: React.MouseEvent) => {
      if (item.disabled) return;
      // Swallow the tail of a multi-click. A chevron row swaps the view in place under a stationary
      // pointer, so the second click of a double-click hit-tests whatever now occupies that band —
      // for the token picker that is a token row, which would change the settlement token from a
      // gesture the user never aimed at anything. Keyboard activation reports detail 0.
      if (e.detail > 1) return;
      if (item.submenu) {
        setPath((p) => [...p.slice(0, depth), item.label]);
        return;
      }
      item.onClick?.();
      onClose();
    },
    [depth, onClose],
  );

  // Arrow keys inside a picker MOVE FOCUS ONLY; Enter/Space chooses (native button activation).
  // Choosing closes the drawer, so selecting as the arrows move would dismiss it on the first
  // press. Paired with the roving tabindex below, this is the ARIA menu model, not radiogroup.
  const handleMenuKeys = useCallback(
    (e: React.KeyboardEvent, section: DrawerSection, index: number) => {
      const keys = ["ArrowDown", "ArrowRight", "ArrowUp", "ArrowLeft"];
      if (!keys.includes(e.key)) return;
      const rows = Array.from(
        (e.currentTarget as HTMLElement).parentElement?.querySelectorAll<HTMLElement>(
          ".gp-drawer__row:not(:disabled)",
        ) ?? [],
      );
      if (rows.length < 2) return;
      e.preventDefault();
      const forward = e.key === "ArrowDown" || e.key === "ArrowRight";
      const here = rows.indexOf(e.currentTarget as HTMLElement);
      rows[(here + (forward ? 1 : -1) + rows.length) % rows.length]?.focus();
    },
    [],
  );

  if (!isOpen) return null;

  return createPortal(
    <div className="gp-drawer" role="presentation">
      <div className="gp-drawer__scrim" onClick={onClose} />

      <aside
        className="gp-drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-label={view.title}
        tabIndex={-1}
        ref={panelRef}
        onKeyDown={trapTab}
      >
        <header className="gp-drawer__head">
          <button
            type="button"
            className="gp-drawer__back"
            aria-label={atRoot ? "Close settings" : `Back to ${title}`}
            onClick={(e) => {
              // Same multi-click guard as the rows: popping re-labels this very button as Close,
              // so an unguarded double-click would pop and then dismiss the whole drawer.
              if (e.detail > 1) return;
              if (atRoot) onClose();
              else pop();
            }}
          >
            <ChevronLeftIconV1 size={20} />
          </button>
          <h2 className="gp-drawer__title">{view.title}</h2>
        </header>

        <div className="gp-drawer__body">
          {view.sections.map((section, i) => {
            const picker = isPicker(section);
            // Roving tabindex: the picker is one tab stop, entered at the active choice.
            const rove = section.items.findIndex((it) => it.selected);
            const tabStop = rove === -1 ? 0 : rove;
            return (
              <section
                className="gp-drawer__section"
                key={section.label ?? `section-${i}`}
              >
                {section.label && (
                  <p className="gp-drawer__section-label">{section.label}</p>
                )}
                <div
                  className="gp-drawer__rows"
                  role={picker ? "menu" : "group"}
                  aria-label={section.label ?? view.title}
                >
                  {section.items.map((item, index) => (
                    <button
                      type="button"
                      key={item.label}
                      className={`gp-drawer__row${item.variant === "danger" ? " gp-drawer__row--danger" : ""}${item.selected ? " gp-drawer__row--selected" : ""}`}
                      role={picker ? "menuitemradio" : undefined}
                      aria-checked={picker ? Boolean(item.selected) : undefined}
                      tabIndex={picker && index !== tabStop ? -1 : undefined}
                      disabled={item.disabled}
                      onClick={(e) => handleItem(item, e)}
                      onKeyDown={
                        picker
                          ? (e) => handleMenuKeys(e, section, index)
                          : undefined
                      }
                    >
                      {item.icon && (
                        <span
                          className="gp-drawer__row-icon"
                          aria-hidden="true"
                        >
                          {item.icon}
                        </span>
                      )}

                      <span className="gp-drawer__row-text">
                        <span className="gp-drawer__row-label">
                          {item.label}
                        </span>
                        {item.hint && (
                          <span className="gp-drawer__row-hint">
                            {item.hint}
                          </span>
                        )}
                      </span>

                      {item.badge && (
                        <span className="gp-drawer__row-badge">
                          {item.badge}
                        </span>
                      )}
                      {item.value && (
                        <span className="gp-drawer__row-value">
                          {item.value}
                        </span>
                      )}

                      {item.submenu && (
                        <span
                          className="gp-drawer__row-chevron"
                          aria-hidden="true"
                        >
                          <ChevronRightIconV1 size={18} />
                        </span>
                      )}
                      {item.external && !item.submenu && (
                        <span
                          className="gp-drawer__row-chevron"
                          aria-hidden="true"
                        >
                          <ExternalLinkIconV1 size={15} />
                        </span>
                      )}
                      {item.selected && (
                        <span
                          className="gp-drawer__row-check"
                          aria-hidden="true"
                        >
                          ✓
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </aside>
    </div>,
    document.body,
  );
}

export default SettingsDrawer;
